/**
 * Notifications slice (PX-W6.2) — the bell's and the centre's shared state:
 * the persisted rows, the unread count, and the live bus upsert.
 *
 * Merge discipline (INV-7): every write MERGES by id — a websocket echo of an
 * action this client just took is an idempotent upsert, never a duplicate or a
 * rollback. `unreadCount` is always RECOMPUTED from the rows in state (server
 * truth on fetch, derived truth between fetch and reconcile) — never a local
 * counter that can drift.
 *
 * `notificationReceived` is the reducer the `EVENT_TYPE_MAP` rows for
 * `notification.created` / `notification.updated` dispatch into (INV-4: those
 * are the only notification events the px backend publishes).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as notificationsHttp from './notifications.http'
import type { ApiNotification } from './types'

/**
 * Preserve the canonical ApiError: the shared transport's interceptor already
 * maps AxiosErrors to it, so re-mapping through `toApiError` would degrade a
 * real 403 to the network fallback. Only strangers go through the mapper.
 */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

/** A row is unread unless the server said otherwise. */
const isUnread = (row: ApiNotification): boolean =>
  row.status !== 'read' && row.status !== 'dismissed'

const recountUnread = (state: NotificationsState): void => {
  state.unreadCount = state.items.filter(isUnread).length
}

/** The cache key: org + active filters, so changing filters forces a refetch. */
export interface NotificationFilters {
  severity?: string
  status?: string
}

const filterKey = (filters: NotificationFilters): string =>
  `${filters.severity ?? ''}|${filters.status ?? ''}`

/** Narrow an unknown bus payload into a row, or null when it carries no id. */
const asRow = (payload: unknown): ApiNotification | null => {
  if (typeof payload !== 'object' || payload === null) return null
  const candidate = payload as Record<string, unknown>
  if (typeof candidate['id'] !== 'string' || candidate['id'] === '') return null
  return payload as ApiNotification
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** The list thunk's payload: rows + meta + the keys they loaded under. */
interface FetchedList {
  orgId: string | null
  filterKeyValue: string
  rows: ApiNotification[]
  unreadCount: number
}

export const fetchNotifications = createAsyncThunk<
  FetchedList,
  NotificationFilters | undefined,
  { rejectValue: ApiError }
>(
  'notifications/fetchNotifications',
  async (filters, { getState, rejectWithValue }) => {
    try {
      const { items = [], meta } = await notificationsHttp.notificationsHttp.list(
        filters?.severity,
        filters?.status
      )
      return {
        orgId: orgKey(getState),
        filterKeyValue: filterKey(filters ?? {}),
        rows: items,
        unreadCount: meta?.unreadCount ?? items.filter(isUnread).length
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (filters, { getState }) =>
      shouldFetch(
        (getState() as { notifications: NotificationsState }).notifications.cache,
        `${orgKey(getState) ?? ''}|${filterKey(filters ?? {})}`
      )
  }
)

/**
 * Mark one row read. Optimistic: the row flips locally (and the count
 * recomputes) the moment the thunk dispatches, then the HTTP response row
 * reconciles it — one PUT, no GET round-trip.
 */
export const markNotificationRead = createAsyncThunk<
  ApiNotification,
  string,
  { rejectValue: ApiError }
>('notifications/markNotificationRead', async (id, { dispatch, rejectWithValue }) => {
  dispatch(rowMarkedRead({ id, readAt: new Date().toISOString() }))
  try {
    return await notificationsHttp.notificationsHttp.markRead(id)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/** Mark every unread row read. Optimistic flip, `{updated}` reconcile. */
export const markAllNotificationsRead = createAsyncThunk<
  number,
  void,
  { rejectValue: ApiError }
>('notifications/markAllNotificationsRead', async (_, { dispatch, rejectWithValue }) => {
  dispatch(allRowsMarkedRead(new Date().toISOString()))
  try {
    const { updated = 0 } = await notificationsHttp.notificationsHttp.markAllRead()
    return updated
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/**
 * Dismiss (hard delete) one row. Optimistic remove; a 404 on the way back is
 * the server agreeing the row is gone — the removal stands. Any other failure
 * also leaves the row removed but invalidates the cache, so the next access
 * restores server truth.
 */
export const dismissNotification = createAsyncThunk<string, string, { rejectValue: ApiError }>(
  'notifications/dismissNotification',
  async (id, { dispatch, rejectWithValue }) => {
    dispatch(rowDismissed(id))
    try {
      await notificationsHttp.notificationsHttp.dismiss(id)
      return id
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

// ---------------------------------------------------------------------------
// State + slice
// ---------------------------------------------------------------------------

export interface NotificationsState {
  items: ApiNotification[]
  unreadCount: number
  cache: FetchCache
  error: ApiError | null
  /** The filters the current items were loaded with. */
  filters: NotificationFilters
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  cache: initialFetchCache,
  error: null,
  filters: {}
}

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /** The WS bus row upsert (EVENT_TYPE_MAP: notification.created/updated). */
    notificationReceived(state, action: PayloadAction<unknown>) {
      const row = asRow(action.payload)
      if (row === null) return
      const existing = state.items.findIndex((item) => item.id === row.id)
      if (existing >= 0) state.items[existing] = { ...state.items[existing], ...row }
      else state.items.unshift(row)
      recountUnread(state)
    },
    /** Optimistic mark-read (dispatched by the thunk before the call). */
    rowMarkedRead(state, action: PayloadAction<{ id: string; readAt: string }>) {
      const row = state.items.find((item) => item.id === action.payload.id)
      if (row !== undefined) {
        row.status = 'read'
        row.readAt = action.payload.readAt
      }
      recountUnread(state)
    },
    /** Optimistic mark-all-read (dispatched by the thunk before the call). */
    allRowsMarkedRead(state, action: PayloadAction<string>) {
      for (const row of state.items) {
        if (isUnread(row)) {
          row.status = 'read'
          row.readAt = action.payload
        }
      }
      recountUnread(state)
    },
    /** Optimistic dismiss-remove (dispatched by the thunk before the call). */
    rowDismissed(state, action: PayloadAction<string>) {
      state.items = state.items.filter((item) => item.id !== action.payload)
      recountUnread(state)
    },
    /** Manual retry affordance: rearm the fetch. */
    listRetried(state) {
      invalidate(state.cache)
    },
    /** A filter change rearms the fetch for the new key. */
    filtersChanged(state, action: PayloadAction<NotificationFilters>) {
      state.filters = action.payload
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action: PayloadAction<FetchedList>) => {
        fetchSucceeded(state.cache, `${action.payload.orgId ?? ''}|${action.payload.filterKeyValue}`)
        state.items = action.payload.rows
        state.unreadCount = action.payload.unreadCount
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(markNotificationRead.fulfilled, (state, action: PayloadAction<ApiNotification>) => {
        const row = asRow(action.payload)
        if (row === null) return
        const existing = state.items.findIndex((item) => item.id === row.id)
        if (existing >= 0) state.items[existing] = { ...state.items[existing], ...row }
        recountUnread(state)
      })
      .addCase(markNotificationRead.rejected, (state) => {
        // The optimistic flip may have been wrong — rearm so the next access
        // restores server truth instead of a drifted local count.
        invalidate(state.cache)
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        // Server truth: N rows updated. The rows are already read locally;
        // the count is what the reconcile owns.
        state.unreadCount = 0
      })
      .addCase(markAllNotificationsRead.rejected, (state) => {
        invalidate(state.cache)
      })
      .addCase(dismissNotification.rejected, (state, action) => {
        // A 404 agrees the row is gone (removal stands); anything else still
        // invalidates so the list heals from the server on next access.
        if (action.payload?.status !== 404) invalidate(state.cache)
      })
  }
})

export const {
  notificationReceived,
  rowMarkedRead,
  allRowsMarkedRead,
  rowDismissed,
  listRetried,
  filtersChanged
} = notificationsSlice.actions
